import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Languages } from 'lucide-react';
import { cn } from '@/lib/utils';
import { currentUiLangCode } from '@/lib/ui-languages';
import { useUiLanguages } from '@/hooks/useUiLanguages';
import { useLanguageStore, type Language as AppLanguage } from '@/stores/language.store';
import { clampMenuLeft, MENU_ESTIMATED_WIDTH } from '@/lib/clamp-menu-position';

type IconVariant = 'globe' | 'languages';

type Props = {
  /** `languages` évite la confusion avec un bouton « Publier » qui utilise déjà Globe. */
  icon?: IconVariant;
  iconClassName?: string;
  buttonClassName?: string;
  /** Position horizontale du menu déroulant */
  menuAlign?: 'end' | 'start';
  /** z-index du panneau (header / modales — défaut au-dessus du contenu principal) */
  menuZIndexClass?: string;
  title?: string;
};

export function UiLanguageMenu({
  icon = 'globe',
  iconClassName = 'h-5 w-5',
  buttonClassName,
  menuAlign = 'end',
  menuZIndexClass = 'z-[200]',
  title,
}: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const uiLanguages = useUiLanguages();
  const activeCode = currentUiLangCode(i18n.language);
  const IconCmp = icon === 'languages' ? Languages : Globe;

  const repositionMenu = useCallback(() => {
    const trigger = containerRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const menuWidth = menuPanelRef.current?.getBoundingClientRect().width ?? MENU_ESTIMATED_WIDTH;
    setMenuPos({
      top: trigger.bottom + 8,
      left: clampMenuLeft(trigger, menuWidth, menuAlign),
    });
  }, [menuAlign]);

  useLayoutEffect(() => {
    if (!open) return;
    repositionMenu();
    const onScrollOrResize = () => repositionMenu();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, repositionMenu]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const node = e.target as Node;
      if (containerRef.current?.contains(node)) return;
      if (menuPanelRef.current?.contains(node)) return;
      setOpen(false);
      setMenuPos(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const handleSelect = (code: string) => {
    const short = code.split('-')[0] as AppLanguage;
    useLanguageStore.getState().setLanguage(short);
    void i18n.changeLanguage(short);
    setOpen(false);
    setMenuPos(null);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((wasOpen) => {
            if (wasOpen) {
              setMenuPos(null);
              return false;
            }
            const trigger = containerRef.current?.getBoundingClientRect();
            if (trigger) {
              setMenuPos({
                top: trigger.bottom + 8,
                left: clampMenuLeft(trigger, MENU_ESTIMATED_WIDTH, menuAlign),
              });
            }
            return true;
          });
        }}
        className={buttonClassName}
        title={title}
        aria-label={t('header.language', 'Language')}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <IconCmp className={iconClassName} />
      </button>
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuPanelRef}
            role="listbox"
            dir="ltr"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
            }}
            className={cn(
              'min-w-[11rem] w-max max-w-[min(20rem,calc(100vw-1rem))] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800',
              menuZIndexClass,
            )}
          >
            {uiLanguages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={activeCode === lang.code}
                onClick={() => handleSelect(lang.code)}
                className={cn(
                  'flex w-full items-center px-4 py-2 text-start text-sm transition-colors',
                  activeCode === lang.code
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                )}
              >
                {lang.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
